import { isEmpty, isNaN, isNumber, isString } from 'lodash';
import { parse } from 'qs';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const parseNumberParam = (value: string): string | number | null => {
  // remove all non-numeric characters
  const numString = value.replace(/\D/g, '');

  const number = numString ? parseInt(numString, 10) : null;

  return isNaN(number) ? null : number;
};

const useQueryUrlFilters = <T>(
  defaultFilters: T,
  parseToNumber?: Array<keyof T>,
  parseToBoolean?: Array<keyof T>,
) => {
  const location = useLocation();

  const filterDefault: T = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const filters = q.get('filters');

    // this logic allow a state where the filters are cleared but we don't fallback to the default filters
    if (typeof filters === 'string' && !filters) {
      return {} as T;
    }

    const parsed = parse(filters ?? '');
    if (isEmpty(parsed)) {
      if (defaultFilters) {
        return defaultFilters;
      }
    }

    // qs parses all values as strings
    // we parse parseToNumber string values that exist and if possible convert them to a number
    if (!isEmpty(parseToNumber)) {
      parseToNumber?.forEach((key: keyof T) => {
        if (isString(parsed[key])) {
          const numberValue = parseNumberParam(parsed[key] as string);

          if (isNumber(numberValue)) {
            parsed[key as string] = numberValue as unknown as string;
          }
        }
      });
    }

    // qs parses all values as strings
    // we parse parseToBoolean string values that exist and if possible convert them to a boolean
    if (!isEmpty(parseToBoolean)) {
      parseToBoolean?.forEach((key) => {
        if (isString(parsed[key])) {
          const booleanValue = (parsed[key] as string).toLowerCase() === 'true';
          parsed[key as string] = booleanValue as unknown as string;
        }
      });
    }

    return parsed as T;
  }, [location.search, parseToNumber, parseToBoolean, defaultFilters]);

  return filterDefault;
};

export default useQueryUrlFilters;
